import React, { useState, useEffect } from 'react';
import { Modal, Spinner, Table, Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

/**
 * Records a receiving event against a PO: per-line "receive now" quantities plus optional
 * batch/expiry on inventory-linked lines. Quantities are validated against each line's
 * remaining amount before the request is sent; server errors surface via apiError.
 *
 * Props: isOpen, toggle, po, onReceive({ purchaseOrderId, notes, lines }), isSubmitting, apiError
 */
const ReceivePurchaseOrderModal = ({ isOpen, toggle, po, onReceive, isSubmitting = false, apiError = '' }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState({});
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setRows({}); setNotes(''); setError(''); }
  }, [isOpen, po?.id]);

  const lineItems = po?.lineItems || [];
  const setRow = (id, field, value) => setRows((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));

  // Only Parts & Consumables lines carry stock (batch/expiry); Asset and Free-text lines don't.
  const isInventory = (li) => li.itemType === 'SPARE_PART';

  const handleSubmit = () => {
    setError('');
    // Client-side guard: nothing may exceed its line's remaining quantity.
    const over = lineItems.find((li) => (Number(rows[li.id]?.quantity) || 0) > (li.remainingQuantity ?? 0));
    if (over) {
      setError(t('planning_purchasing.receive_exceeds_remaining',
        '"{{item}}" exceeds the remaining quantity ({{n}} left)',
        { item: over.description || over.reference || '-', n: over.remainingQuantity ?? 0 }));
      return;
    }
    const lines = lineItems
      .map((li) => {
        const r = rows[li.id] || {};
        const qty = Number(r.quantity) || 0;
        if (qty <= 0) return null;
        return { lineItemId: li.id, quantity: qty, batchNumber: r.batchNumber || null, expiryDate: r.expiryDate || null };
      })
      .filter(Boolean);
    if (lines.length === 0) return;
    onReceive({ purchaseOrderId: po.id, notes: notes || null, lines });
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}>
        <div className="modal-header">
          <h5 className="modal-title">{t('planning_purchasing.receive_items', 'Receive Items')} — {po?.poNumber}</h5>
          <button type="button" className="btn-close" onClick={toggle} />
        </div>
        <div className="modal-body">
          {(apiError || error) && <Alert color={apiError ? 'danger' : 'warning'}>{apiError || error}</Alert>}
          <div className="table-responsive">
            <Table size="sm" bordered className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>{t('shared.description', 'Description')}</th>
                  <th className="text-center">{t('planning_purchasing.ordered', 'Ordered')}</th>
                  <th className="text-center">{t('planning_purchasing.received', 'Received')}</th>
                  <th className="text-center">{t('planning_purchasing.remaining', 'Remaining')}</th>
                  <th style={{ width: 110 }}>{t('planning_purchasing.receive_now', 'Receive now')}</th>
                  <th style={{ width: 120 }}>{t('planning_purchasing.batch', 'Batch')}</th>
                  <th style={{ width: 140 }}>{t('planning_purchasing.expiry', 'Expiry')}</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li) => (
                  <tr key={li.id}>
                    <td>
                      {li.description || li.reference || '-'}
                      {/* Unlinked parts lines are auto-created in Inventory on receipt —
                          surface that so the receiver knows where the stock will land. */}
                      {isInventory(li) && !li.itemId && (
                        <small className="d-block text-info">
                          {t('planning_purchasing.new_part_on_receipt', "This item doesn't exist in Inventory yet — a new part will be created on receipt.")}
                        </small>
                      )}
                    </td>
                    <td className="text-center">{li.quantity}</td>
                    <td className="text-center">{li.receivedQuantity ?? 0}</td>
                    <td className="text-center">{li.remainingQuantity ?? 0}</td>
                    <td>
                      <input type="number" min="0" max={li.remainingQuantity ?? 0} className="form-control form-control-sm"
                             value={rows[li.id]?.quantity || ''} disabled={(li.remainingQuantity ?? 0) <= 0}
                             onChange={(e) => setRow(li.id, 'quantity', e.target.value)} />
                    </td>
                    <td>
                      <input className="form-control form-control-sm" disabled={!isInventory(li)}
                             title={!isInventory(li) ? t('planning_purchasing.batch_expiry_parts_only', 'Batch and expiry can only be entered on Parts & Consumables lines — they create stock batches in Inventory.') : undefined}
                             value={rows[li.id]?.batchNumber || ''} onChange={(e) => setRow(li.id, 'batchNumber', e.target.value)} />
                    </td>
                    <td>
                      <input type="date" className="form-control form-control-sm" disabled={!isInventory(li)}
                             title={!isInventory(li) ? t('planning_purchasing.batch_expiry_parts_only', 'Batch and expiry can only be entered on Parts & Consumables lines — they create stock batches in Inventory.') : undefined}
                             value={rows[li.id]?.expiryDate || ''} onChange={(e) => setRow(li.id, 'expiryDate', e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="mt-2">
            <label className="form-label small">{t('planning_purchasing.receipt_notes', 'Receipt notes')}</label>
            <input className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <small className="text-muted d-block mt-2">
            <FeatherIconInfo /> {t('planning_purchasing.receive_hint', 'Stock for Parts & Consumables lines updates automatically on receipt.')}
            {' '}{t('planning_purchasing.batch_expiry_parts_only', 'Batch and expiry can only be entered on Parts & Consumables lines — they create stock batches in Inventory.')}
          </small>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-secondary" onClick={toggle}>{t('shared.cancel', 'Cancel')}</button>
          <button type="button" className="btn btn-success" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? <><Spinner size="sm" className="me-2" />{t('planning_purchasing.receiving', 'Receiving...')}</> : t('planning_purchasing.confirm_receive', 'Confirm Receipt')}
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

const FeatherIconInfo = () => <i className="mdi mdi-information-outline" />;

export default ReceivePurchaseOrderModal;
